import React, { useEffect, useRef, useState } from 'react';
import { getAuthToken } from '../api/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Overlay de modo mantenimiento para los despliegues.
 *
 * Consulta GET /api/sistema/mantenimiento (público) cada 30 s, y cada 5 s
 * mientras hay un mantenimiento anunciado o en curso. Cuando dirección
 * activa el modo:
 *  1. Aviso (banner con cuenta atrás) mientras quedan segundos.
 *  2. Bloqueo (overlay a pantalla completa) cuando llega a 0 o el backend deja
 *     de responder (reinicio en curso).
 *  3. Al terminar el deploy (flag desactivado y backend respondiendo de nuevo),
 *     recarga la página para cargar la versión nueva.
 */
const MantenimientoOverlay = () => {
  const [info, setInfo] = useState(null);     // respuesta del backend
  const [cuenta, setCuenta] = useState(null); // segundos locales hasta el bloqueo
  const [bloqueado, setBloqueado] = useState(false);
  const [accionando, setAccionando] = useState(false); // posponer/cancelar en curso
  const estuvoActivoRef = useRef(false);      // ¿hemos estado alguna vez en mantenimiento?

  const authHeaders = () => {
    const tk = getAuthToken();
    return { 'Authorization': `Bearer ${tk}`, 'Content-Type': 'application/json' };
  };

  const posponer = async () => {
    setAccionando(true);
    try {
      const res = await fetch(`${API_URL}/api/sistema/mantenimiento/posponer`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ segundos: 15 }),
      });
      const data = await res.json();
      if (res.ok && data.segundos_restantes != null) setCuenta(data.segundos_restantes);
    } catch { /* el siguiente poll resincroniza */ }
    finally { setAccionando(false); }
  };

  const cancelar = async () => {
    setAccionando(true);
    try {
      await fetch(`${API_URL}/api/sistema/mantenimiento/cancelar`, { method: 'POST', headers: authHeaders() });
      estuvoActivoRef.current = false; // cancelado: no recargar
      setInfo(null); setCuenta(null); setBloqueado(false);
    } catch { /* el poll lo reflejará */ }
    finally { setAccionando(false); }
  };

  // Polling del estado: lento (30 s) en reposo, rápido (5 s) con un
  // mantenimiento anunciado (cuenta atrás) o mientras el backend se reinicia
  useEffect(() => {
    let cancel = false;
    let temporizador = null;
    const tick = async () => {
      let rapido = false;
      try {
        const res = await fetch(`${API_URL}/api/sistema/mantenimiento`, { cache: 'no-store' });
        if (!res.ok) throw new Error('bad status');
        const data = await res.json();
        if (cancel) return;
        if (data.activo) {
          rapido = true;
          estuvoActivoRef.current = true;
          setInfo(data);
          const s = data.segundos_restantes ?? 0;
          setCuenta(prev => (prev == null ? s : Math.min(prev, s))); // no “rebotar” hacia arriba
          if (s <= 0) setBloqueado(true);
        } else {
          // Mantenimiento desactivado
          if (estuvoActivoRef.current) {
            // El deploy terminó → recargar para coger la versión nueva
            window.location.reload();
            return;
          }
          setInfo(null);
          setCuenta(null);
          setBloqueado(false);
        }
      } catch {
        if (cancel) return;
        // Backend no responde: si ya estábamos en mantenimiento, es el reinicio → bloquear
        if (estuvoActivoRef.current) {
          setBloqueado(true);
          rapido = true;
        }
      }
      if (!cancel) temporizador = setTimeout(tick, rapido ? 5000 : 30000);
    };
    tick();
    return () => { cancel = true; clearTimeout(temporizador); };
  }, []);

  // Cuenta atrás local (1s) durante la fase de aviso
  useEffect(() => {
    if (bloqueado || cuenta == null || !info?.activo) return;
    if (cuenta <= 0) { setBloqueado(true); return; }
    const t = setTimeout(() => setCuenta(c => (c == null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cuenta, bloqueado, info]);

  // --- Bloqueo a pantalla completa ---
  if (bloqueado) {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <div style={spinnerStyle} />
          <h2 style={{ margin: '16px 0 8px', fontSize: 20, color: '#1A9169' }}>Aplicando actualización…</h2>
          <p style={{ margin: 0, color: '#475569', fontSize: 14, maxWidth: 360 }}>
            Estamos actualizando la aplicación. En unos segundos volverá a estar disponible
            y la página se recargará automáticamente. No cierres esta ventana.
          </p>
        </div>
      </div>
    );
  }

  // --- Aviso con cuenta atrás ---
  if (info?.activo && cuenta != null && cuenta > 0) {
    return (
      <div style={bannerStyle}>
        <span style={{ marginRight: 6 }}>
          <span style={{ fontWeight: 600 }}>{info.mensaje}</span>{' '}
          La aplicación se bloqueará en <b>{cuenta}s</b>. Guarda tu trabajo.
        </span>
        <button onClick={posponer} disabled={accionando} style={btnBanner('#1f2937', '#fde68a')}>
          +15 segundos
        </button>
        <button onClick={cancelar} disabled={accionando} style={btnBanner('#fff', '#dc2626')}>
          Cancelar actualización
        </button>
      </div>
    );
  }

  return null;
};

const btnBanner = (color, bg) => ({
  marginLeft: 8, padding: '4px 10px', borderRadius: 6, border: 'none',
  background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer',
});

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 999999,
  background: 'rgba(15,23,42,0.78)', backdropFilter: 'blur(3px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const cardStyle = {
  background: '#fff', borderRadius: 16, padding: '32px 28px', textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', alignItems: 'center',
};
const spinnerStyle = {
  width: 40, height: 40, borderRadius: '50%',
  border: '4px solid #e2e8f0', borderTopColor: '#1A9169',
  animation: 'mantSpin 0.9s linear infinite',
};
const bannerStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, zIndex: 999998,
  background: '#f59e0b', color: '#1f2937', padding: '10px 16px',
  fontSize: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 4,
};

// Keyframes del spinner (inyectados una vez)
if (typeof document !== 'undefined' && !document.getElementById('mant-spin-kf')) {
  const style = document.createElement('style');
  style.id = 'mant-spin-kf';
  style.textContent = '@keyframes mantSpin{to{transform:rotate(360deg)}}';
  document.head.appendChild(style);
}

export default MantenimientoOverlay;
